/* ==========================================================================
   Daily Nouri — cart controller
   --------------------------------------------------------------------------
   Uses ONLY Shopify's own cart AJAX API and the Section Rendering API.
   No Recharge endpoints are called from the storefront; selling plans are
   swapped through /cart/change.js exactly as any Shopify selling plan is.
   ========================================================================== */

(function () {
  'use strict';

  // This file is referenced by BOTH dn-cart-drawer and dn-cart-page. On the
  // cart page both sections render, so guard against binding twice.
  if (window.DNCart) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTIONS = ['dn-cart-drawer', 'dn-cart-page'];
  var UNDO_MS = 8000;

  var undoBuffer = null;   // { label, items: [addPayload, ...] }
  var undoTimer = null;
  var inFlight = false;

  /* ---------- helpers ---------------------------------------------------- */

  function url(path) {
    return ROOT.replace(/\/$/, '') + path;
  }

  function present(list) {
    // Section ids as Shopify actually renders them. The drawer is rendered
    // with {% section %} so its id is literally "dn-cart-drawer". The cart
    // PAGE comes from templates/cart.json, so Shopify gives it a generated id
    // like "template--1234__main" — find it by its [data-dn-page] marker.
    // Missing this meant /cart never repainted after a change.
    var ids = list.filter(function (id) {
      return document.getElementById('shopify-section-' + id);
    });
    var pageEl = document.querySelector('[data-dn-page]');
    var host = pageEl && pageEl.closest('[id^="shopify-section-"]');
    if (host) {
      var pid = host.id.replace(/^shopify-section-/, '');
      if (ids.indexOf(pid) === -1) ids.push(pid);
    }
    return ids;
  }

  function post(path, body, skipSections) {
    var ids = present(SECTIONS);
    if (ids.length && !skipSections) body.sections = ids.join(',');
    return fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw Object.assign(new Error(data.description || 'Cart error'), { data: data });
        return data;
      });
    });
  }

  function paint(data) {
    if (!data || !data.sections) return;
    // The drawer element lives INSIDE its section, so replacing the section's
    // innerHTML resets aria-hidden and would snap the drawer shut on every
    // cart update. Capture the open state and restore it.
    var d = drawer();
    var wasOpen = !!d && d.getAttribute('aria-hidden') === 'false';

    Object.keys(data.sections).forEach(function (id) {
      var host = document.getElementById('shopify-section-' + id);
      if (!host) return;
      var doc = new DOMParser().parseFromString(data.sections[id], 'text/html');
      var fresh = doc.getElementById('shopify-section-' + id);
      if (fresh) host.innerHTML = fresh.innerHTML;
    });

    if (wasOpen) {
      var next = drawer();
      if (next) {
        next.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflow = 'hidden';
      }
    }

    renderUndo();
    document.dispatchEvent(new CustomEvent('dn:cart:updated'));
  }

  function refresh() {
    var ids = present(SECTIONS);
    if (!ids.length) return Promise.resolve();
    // Render from the CURRENT page so template sections (the /cart page's
    // generated id) resolve; "/" only knows the homepage's sections.
    return fetch(window.location.pathname + '?sections=' + ids.join(','), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (sections) { paint({ sections: sections }); });
  }

  function busy(el, state) {
    var line = el && el.closest('[data-dn-line]');
    if (line) line.setAttribute('data-dn-busy', state ? 'true' : 'false');
  }

  /* ---------- quantity reconciliation -------------------------------------
     1 bottle = 1 month, so a subscription line's quantity MUST equal its
     plan's term. A double add-to-cart pushed quantity to 2/4/6 while the line
     stayed on the monthly plan, so the cart both displayed and charged
     monthly-price x N — e.g. $208.14 for a "6-month" line that should be
     $166.50. This repairs any line that has drifted, including carts already
     in the wild, and runs before the shopper sees a price.
     ---------------------------------------------------------------------- */

  var reconciling = false;

  /* One-time purchases are allowed. Set window.DN_ALLOW_ONETIME = false in
     theme.liquid to go back to subscription-only. */
  var ALLOW_ONETIME = window.DN_ALLOW_ONETIME !== false;

  /* Did the shopper explicitly pick a one-time option in this form? Matches
     Recharge's "onetime" radio and any radio labelled "One time". */
  function choseOneTime(form) {
    if (!form) return false;
    var picked = form.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < picked.length; i++) {
      var r = picked[i];
      if (/^(onetime|one-time|one_time|once)$/i.test(r.value || '')) return true;
      var lbl = r.closest('label') || (r.id && form.querySelector('label[for="' + r.id + '"]'));
      if (lbl && /one[\s-]?time/i.test(lbl.textContent || '')) return true;
    }
    return false;
  }

  function planTerm(name) {
    var m = String(name || '').match(/\d+/);
    var n = m ? parseInt(m[0], 10) : 1;
    return n > 0 ? n : 1;
  }

  /* Resolve the MONTHLY plan id for a variant from Shopify's /products/<handle>.js.
     Shape matters: plan names live in product.selling_plan_groups[].selling_plans[],
     and variant.selling_plan_allocations[] carries only selling_plan_id — NOT a
     nested selling_plan object. Reading a.selling_plan.name silently threw and
     the catch swallowed it, which is why plan-less lines were never repaired. */
  function monthlyPlanFromProduct(p, variantId) {
    var names = {};
    (p.selling_plan_groups || []).forEach(function (g) {
      (g.selling_plans || []).forEach(function (sp) { names[String(sp.id)] = sp.name || ''; });
    });
    var v = (p.variants || []).filter(function (x) { return String(x.id) === String(variantId); })[0];
    var best = null, bestMonths = Infinity;
    ((v && v.selling_plan_allocations) || []).forEach(function (a) {
      var id = a.selling_plan_id || (a.selling_plan && a.selling_plan.id);
      if (!id) return;
      var n = planTerm(names[String(id)] || (a.selling_plan && a.selling_plan.name) || '');
      if (n < bestMonths) { bestMonths = n; best = id; }
    });
    return best;
  }

  /* Zero-network path: the drawer section emits {variant_id: monthly_plan_id}
     for the current page's product (see dn-cart-drawer.liquid). */
  function monthlyPlanFromPage(variantId) {
    var el = document.querySelector('[data-dn-page-plans]');
    if (!el) return null;
    try { var m = JSON.parse(el.textContent || '{}'); return m[String(variantId)] || null; } catch (e) { return null; }
  }

  /* Cache of product handle -> monthly selling_plan id, so repeated
     reconciles don't re-fetch the same product JSON. */
  var monthlyPlanCache = {};

  function monthlyPlanFor(handle, variantId) {
    var fromPage = monthlyPlanFromPage(variantId);
    if (fromPage) return Promise.resolve(fromPage);
    var key = handle + ':' + variantId;
    if (monthlyPlanCache[key] !== undefined) return Promise.resolve(monthlyPlanCache[key]);
    return fetch(url('/products/' + handle + '.js'), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var best = monthlyPlanFromProduct(p, variantId);
        monthlyPlanCache[key] = best;
        return best;
      })
      .catch(function () { monthlyPlanCache[key] = null; return null; });
  }

  function reconcile() {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;

    return fetch(url('/cart.js'), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];

        /* 1. Lines with a plan but a drifted quantity (1 bottle = 1 month). */
        /* Multi-month plans ship whole sets (3-month = 3 bottles), so the
           quantity must be a multiple of the term. Monthly lines can be any
           quantity now that the cart has a stepper. */
        var qtyFixes = items.filter(function (i) {
          if (!i.selling_plan_allocation) return false;
          var term = planTerm(i.selling_plan_allocation.selling_plan.name);
          return term > 1 && i.quantity % term !== 0;
        });

        /* 2. Lines with NO plan at all. This store is subscription-only, so a
           plan-less line is always a failed add — whatever path added it
           (theme AJAX, Recharge widget, permalink, a stale form). Move it onto
           the product's monthly plan right here, where nothing can bypass us. */
        var planFixes = ALLOW_ONETIME ? [] : items.filter(function (i) { return !i.selling_plan_allocation && i.handle; });

        if (!qtyFixes.length && !planFixes.length) return false;

        var chain = Promise.resolve();
        qtyFixes.forEach(function (i) {
          chain = chain.then(function () {
            var term = planTerm(i.selling_plan_allocation.selling_plan.name);
            return post('/cart/change.js', {
              id: i.key,
              quantity: Math.max(term, Math.round(i.quantity / term) * term)
            });
          });
        });
        planFixes.forEach(function (i) {
          chain = chain.then(function () {
            return monthlyPlanFor(i.handle, i.variant_id).then(function (planId) {
              if (!planId) return null;
              return post('/cart/change.js', { id: i.key, quantity: 1, selling_plan: planId });
            });
          });
        });
        return chain.then(function () { return true; });
      })
      .catch(function () { return false; })
      .then(function (changed) {
        reconciling = false;
        return syncQuizCode().then(function (c2) { return changed || c2; });
      });
  }

  /* ---------- quiz code: removed everywhere --------------------------------
     FIRSTORDER15 is retired from the cart. If anything still applies it (an
     old quiz link, a landing CTA, a /discount/ URL), the cart strips it, so
     shoppers only ever see plan savings. */
  var QUIZ_CODES = (window.DN_QUIZ_CODES || ['FIRSTORDER15']).map(function (c) { return c.toUpperCase(); });

  function syncQuizCode() {
    return fetch(url('/cart.js'), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var applied = (cart.discount_codes || []).some(function (d) {
          return QUIZ_CODES.indexOf(String(d.code || '').toUpperCase()) !== -1;
        });
        if (!applied) return false;
        try { sessionStorage.removeItem('dn_quiz_code'); } catch (e) {}
        return post('/cart/update.js', { discount: '' }, true).then(function () { return true; });
      })
      .catch(function () { return false; });
  }

  /* ---------- drawer ------------------------------------------------------ */

  function drawer() { return document.querySelector('[data-dn-drawer]'); }

  function openDrawer() {
    var d = drawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    var focusable = d.querySelector('button, [href], input');
    if (focusable) focusable.focus({ preventScroll: true });

    /* Repair a drifted quantity before the shopper reads a price. */
    reconcile().then(function (changed) { if (changed) refresh(); });
  }

  function closeDrawer() {
    var d = drawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  /* ---------- undo -------------------------------------------------------- */

  function renderUndo() {
    document.querySelectorAll('[data-dn-undo-slot]').forEach(function (slot) {
      if (!undoBuffer) { slot.innerHTML = ''; return; }
      slot.innerHTML =
        '<div class="dn-undo"><span>' + undoBuffer.label + ' removed</span>' +
        '<button type="button" data-dn-undo>Undo</button></div>';
    });
  }

  function armUndo(label, items) {
    undoBuffer = { label: label, items: items };
    clearTimeout(undoTimer);
    undoTimer = setTimeout(function () { undoBuffer = null; renderUndo(); }, UNDO_MS);
    renderUndo();
  }

  function doUndo() {
    if (!undoBuffer) return;
    var items = undoBuffer.items;
    undoBuffer = null;
    clearTimeout(undoTimer);
    renderUndo();
    post('/cart/add.js', { items: items }).then(paint).catch(function () { refresh(); });
  }

  /* ---------- actions ------------------------------------------------------ */

  /* Cadence switching — built for speed and for rapid re-taps.
     - Only lines NOT already on the target plan+qty are touched.
     - Only the LAST request asks Shopify to re-render the cart sections
       (section rendering is the slow part; doing it per line doubled the wait).
     - Taps during a switch don't stack up racing requests: the newest tap is
       remembered and runs once the current one lands, then paints once. */
  /* ---------- v4 standard cart: per-line plan switch + qty stepper ------ */

  function switchPlan(btn) {
    var line = btn.closest('[data-dn-line]');
    if (!line) return;
    busy(btn, true);
    post('/cart/change.js', {
      id: line.getAttribute('data-dn-key'),
      quantity: Number(btn.getAttribute('data-dn-switch-qty')) || 1,
      /* plan "0" = One-time: null tells Shopify to drop the selling plan. */
      selling_plan: Number(btn.getAttribute('data-dn-switch-plan')) || null
    }).then(function (data) {
      if (btn.getAttribute('data-dn-switch-plan') !== '0') return paint(data);
      /* Fallback: if Shopify kept the plan, swap the line for a plain add. */
      var variant = Number(line.getAttribute('data-dn-variant'));
      var qty = Number(btn.getAttribute('data-dn-switch-qty')) || 1;
      var stuck = (data.items || []).some(function (i) {
        return i.variant_id === variant && i.selling_plan_allocation;
      });
      if (!stuck) return paint(data);
      var key = (data.items || []).filter(function (i) {
        return i.variant_id === variant && i.selling_plan_allocation;
      })[0].key;
      return post('/cart/change.js', { id: key, quantity: 0 }, true)
        .then(function () { return post('/cart/add.js', { items: [{ id: variant, quantity: qty }] }); })
        .then(paint);
    }).catch(function () { return refresh(); });
  }

  function stepQty(btn) {
    var line = btn.closest('[data-dn-line]');
    if (!line) return;
    var next = (Number(line.getAttribute('data-dn-qty')) || 1) + Number(btn.getAttribute('data-dn-qty-step'));
    if (next < 1) return;
    line.setAttribute('data-dn-qty', next);
    var shown = btn.parentNode && btn.parentNode.querySelector('span');
    if (shown) shown.textContent = next;
    busy(btn, true);
    post('/cart/change.js', { id: line.getAttribute('data-dn-key'), quantity: next })
      .then(paint).catch(function () { return refresh(); });
  }

  var cadenceBusy = false;
  var cadenceQueued = null;

  function setCadenceBusy(state) {
    document.querySelectorAll('[data-dn-lines], .dn-upgrade').forEach(function (el) {
      el.setAttribute('data-dn-busy', state ? 'true' : 'false');
    });
  }

  function changeCadence(btn) {
    var months = Number(btn.getAttribute('data-dn-cadence-months')) || 1;
    var map = {};
    try { map = JSON.parse(btn.getAttribute('data-dn-cadence-map') || '{}'); } catch (e) {}

    // Instant visual response, before any network.
    document.querySelectorAll('[data-dn-cadence-months]').forEach(function (b) {
      b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
    });

    if (cadenceBusy) { cadenceQueued = btn; return; }

    var lines = Array.prototype.slice.call(document.querySelectorAll('[data-dn-line][data-dn-plan]'))
      .filter(function (el) { return el.getAttribute('data-dn-plan') !== '0'; });

    var jobs = lines.map(function (el) {
      var planId = map[el.getAttribute('data-dn-variant')];
      if (!planId) return null;
      var samePlan = String(el.getAttribute('data-dn-plan')) === String(planId);
      var sameQty = Number(el.getAttribute('data-dn-months')) === months;
      if (samePlan && sameQty) return null;
      return { key: el.getAttribute('data-dn-key'), planId: planId };
    }).filter(Boolean);

    if (!jobs.length) return;

    cadenceBusy = true;
    setCadenceBusy(true);

    jobs.reduce(function (chain, job, i) {
      var last = i === jobs.length - 1;
      return chain.then(function () {
        return post('/cart/change.js', { id: job.key, quantity: months, selling_plan: job.planId }, !last);
      });
    }, Promise.resolve())
      .then(paint)
      .catch(function () { return refresh(); })
      .then(function () {
        cadenceBusy = false;
        setCadenceBusy(false);
        if (cadenceQueued) {
          var next = cadenceQueued;
          cadenceQueued = null;
          // Re-find the button in the freshly painted DOM by its term.
          var fresh = document.querySelector('[data-dn-cadence-months="' + next.getAttribute('data-dn-cadence-months') + '"]');
          if (fresh) changeCadence(fresh);
        }
      });
  }

  function removeLine(btn) {
    var line = btn.closest('[data-dn-line]');
    if (!line) return;
    var label = line.getAttribute('data-dn-title') || 'Item';
    var keys = [line.getAttribute('data-dn-key')];
    var partner = line.getAttribute('data-dn-partner-key');
    if (partner) keys.push(partner);

    busy(btn, true);

    // Capture enough to put it back exactly as it was.
    fetch(url('/cart.js'))
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var restore = cart.items
          .filter(function (i) { return keys.indexOf(i.key) !== -1; })
          .map(function (i) {
            var payload = { id: i.variant_id, quantity: i.quantity };
            if (i.selling_plan_allocation) {
              payload.selling_plan = i.selling_plan_allocation.selling_plan.id;
            }
            // Preserve line properties on undo, minus Recharge's legacy
            // interval keys (see addFromForm) so we never resurrect stale ones.
            if (i.properties && Object.keys(i.properties).length) {
              var kept = {};
              Object.keys(i.properties).forEach(function (k) {
                if (!/^(shipping_interval|charge_interval|subscription_id|_rc_)/i.test(k)) kept[k] = i.properties[k];
              });
              if (Object.keys(kept).length) payload.properties = kept;
            }
            return payload;
          });

        var updates = {};
        keys.forEach(function (k) { updates[k] = 0; });

        return post('/cart/update.js', { updates: updates }).then(function (data) {
          armUndo(label, restore);
          paint(data);
        });
      })
      .catch(function () { refresh(); });
  }

  function addCrossSell(btn) {
    var variantId = Number(btn.getAttribute('data-variant') || btn.getAttribute('data-dn-variant'));
    if (!variantId) return;
    btn.disabled = true;
    var originalLabel = btn.textContent;
    btn.textContent = 'Adding…';

    // Each product has its OWN distinct selling_plan ids, even for "the
    // same" term — use the plan/months rendered specifically for THIS
    // product (matched server-side), not the hero line's plan id.
    // Plan "0" means one-time purchase. Never borrow the hero line's plan id —
    // it belongs to a different product and Shopify rejects it.
    var planId = Number(btn.getAttribute('data-dn-crosssell-plan')) || null;
    var months = Number(btn.getAttribute('data-dn-crosssell-months')) || 1;

    var payload = { id: variantId, quantity: months };
    if (planId) payload.selling_plan = planId;

    post('/cart/add.js', { items: [payload] })
      .then(paint)
      .catch(function () {
        btn.disabled = false;
        btn.textContent = originalLabel;
        refresh();
      });
  }

  function applyCode(form) {
    var input = form.querySelector('input[name="discount"]');
    var errorEl = form.parentNode.querySelector('[data-dn-code-error]');
    var code = (input.value || '').trim();
    if (!code) return;
    if (errorEl) errorEl.hidden = true;

    // Shopify has no AJAX discount endpoint; hitting /discount/CODE sets the
    // cart's discount, then we re-render. See INSTALL.md — verify on staging.
    fetch(url('/discount/' + encodeURIComponent(code)), { method: 'GET' })
      .then(function () { return fetch(url('/cart.js')); })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var ok = cart.cart_level_discount_applications &&
                 cart.cart_level_discount_applications.length > 0;
        if (!ok && errorEl) errorEl.hidden = false;
        return refresh();
      })
      .catch(function () { if (errorEl) errorEl.hidden = false; });
  }

  /* ---------- wiring -------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var t = e.target;

    /* Progressive disclosure: reveal the full delivery list only on request. */
    var toggle = t.closest && t.closest('[data-dn-cadence-toggle]');
    if (toggle) {
      e.preventDefault();
      var wrap = toggle.closest('.dn-cadence');
      var panel = wrap && wrap.querySelector('[data-dn-cadence-panel]');
      if (panel) {
        var open = panel.hasAttribute('hidden');
        if (open) { panel.removeAttribute('hidden'); } else { panel.setAttribute('hidden', ''); }
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      return;
    }

    var sw = t.closest && t.closest('[data-dn-switch-plan]');
    if (sw) { e.preventDefault(); switchPlan(sw); return; }

    var step = t.closest && t.closest('[data-dn-qty-step]');
    if (step) { e.preventDefault(); if (!step.disabled) stepQty(step); return; }

    var cadence = t.closest && t.closest('[data-dn-cadence-months]');
    if (cadence) { e.preventDefault(); if (!inFlight) changeCadence(cadence); return; }

    var rm = t.closest && t.closest('[data-dn-remove]');
    if (rm) { e.preventDefault(); removeLine(rm); return; }

    var undo = t.closest && t.closest('[data-dn-undo]');
    if (undo) { e.preventDefault(); doUndo(); return; }

    var cross = t.closest && t.closest('[data-dn-add-crosssell]');
    if (cross) { e.preventDefault(); addCrossSell(cross); return; }

    var close = t.closest && t.closest('[data-dn-close]');
    if (close) { e.preventDefault(); closeDrawer(); return; }
  });

  document.addEventListener('submit', function (e) {
    var form = e.target.closest && e.target.closest('[data-dn-code-form]');
    if (!form) return;
    e.preventDefault();
    applyCode(form);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ---------- PDP: subscription-first, no frequency UI ----------------------
     The term is chosen in the cart now, so the PDP widget is hidden. Before
     hiding it we select the subscription option, so "Start Now" adds a
     subscription by default instead of a one-time purchase. Reading the plan
     from the form means no hardcoded IDs — this works for every product.
     ------------------------------------------------------------------------ */

  function subscriptionFirst() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      // Once the shopper touches the purchase options, never override them.
      if (form.getAttribute('data-dn-touched')) return;
      if (!form.getAttribute('data-dn-watch')) {
        form.setAttribute('data-dn-watch', '1');
        form.addEventListener('change', function (ev) {
          if (ev.isTrusted) form.setAttribute('data-dn-touched', '1');
        });
        form.addEventListener('pointerdown', function () { form.setAttribute('data-dn-touched', '1'); });
      }
      var plan = null;

      /* Recharge's widget radios (rc-radio__input) carry 'onetime' for the
         one-off option; anything else is a subscription plan. */
      form.querySelectorAll('input.rc-radio__input[type="radio"]').forEach(function (r) {
        if (!plan && r.value && r.value !== 'onetime') plan = r;
      });

      /* Fall back to Shopify-native selling plan radios. */
      if (!plan) {
        form.querySelectorAll('input[name="selling_plan"]').forEach(function (r) {
          if (!plan && r.value && r.value !== '0') plan = r;
        });
      }

      if (!plan || plan.checked) return;

      plan.checked = true;
      plan.dispatchEvent(new Event('change', { bubbles: true }));
      plan.dispatchEvent(new Event('input', { bubbles: true }));
      plan.dispatchEvent(new Event('click', { bubbles: true }));
    });
  }

  /* ---------- PDP purchase option ----------------------------------------
     The theme renders its own Subscribe / One-time selector. We don't add a
     second one; we just watch which option the shopper taps and record it on
     every add form as data-dn-mode, which addFromForm honours. Matching on the
     option's text keeps this independent of the theme's markup.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    if (!e.isTrusted || !e.target.closest) return;
    if (e.target.closest('[data-dn-drawer], .dn-cart')) return;
    var el = e.target, picked = null;
    for (var i = 0; el && i < 8; i++, el = el.parentElement) {
      var t = (el.textContent || '').replace(/\s+/g, ' ');
      if (t.length > 400) break;
      var one = /one[\s-]?time/i.test(t), sub = /subscri/i.test(t);
      if (one && !sub) { picked = 'onetime'; break; }
      if (sub && !one) { picked = 'sub'; break; }
    }
    if (!picked) return;
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (f) {
      f.setAttribute('data-dn-mode', picked);
      f.setAttribute('data-dn-touched', '1');
    });
  }, true);

  document.addEventListener('DOMContentLoaded', subscriptionFirst);
  /* Recharge renders its widget asynchronously; catch it when it appears. */
  document.addEventListener('DOMContentLoaded', function () {
    var tries = 0;
    var poll = setInterval(function () {
      subscriptionFirst();
      if (++tries > 20) clearInterval(poll);
    }, 250);
  });

  /* Open the drawer after any theme add-to-cart that we can observe. */
  document.addEventListener('dn:cart:open', openDrawer);

  /* Repair carts that are ALREADY broken in the wild: a returning shopper may
     have a line sitting at quantity 4 or 6 on the monthly plan. Fix it on load
     so the drawer, the cart page and checkout all agree. */
  document.addEventListener('DOMContentLoaded', function () {
    reconcile().then(function (changed) { if (changed) refresh(); });
  });

  /* ---------- own the add-to-cart ------------------------------------------
     The theme delegated add-to-cart to an app drawer that is now disabled, so
     no handler remains. We intercept the product form ourselves: add via AJAX,
     then open the drawer. Works on PDP forms and sticky bars alike.
     ------------------------------------------------------------------------ */

  function addFromForm(form, btn) {
    if (inFlight) return;
    inFlight = true;

    btn = btn || form.querySelector('[type="submit"], [name="add"]');
    var label = btn ? btn.innerHTML : null;
    if (btn) { btn.disabled = true; btn.innerHTML = 'Adding…'; }

    var data = new FormData(form);
    /* One tap = one bottle. Quantity is no longer a shopper choice — the term
       is picked in the cart — and /cart/add.js INCREMENTS an existing line, so
       without this a second tap silently became a "2-month" line. */
    data.set('quantity', '1');

    /* Strip Recharge's LEGACY widget properties. The hidden rc_widget still
       emits properties[shipping_interval_frequency] etc. from the PDP form.
       On Shopify Checkout Integration the selling_plan alone drives the
       subscription; these properties are dead weight that (a) print as raw
       key:value text on the checkout line and (b) go stale the moment the
       cadence changes ("30 Days" on a 3-month plan). */
    Array.prototype.slice.call(data.keys()).forEach(function (k) {
      if (/^properties\[(shipping_interval|charge_interval|subscription_id|_rc_)/i.test(k)) data.delete(k);
    });

    var ids = present(SECTIONS);
    if (ids.length) data.append('sections', ids.join(','));

    /* GUARANTEE a subscription. The hidden Recharge widget is supposed to
       supply selling_plan, but when it doesn't (markup change, race, the
       radio not yet mounted) the add lands as a ONE-TIME purchase: checkout
       shows no plan, and the cadence control — which needs a subscription
       line to build from — renders nothing. Resolve the monthly plan for this
       variant from the product JSON and attach it ourselves. */
    var ensurePlan = Promise.resolve();
    var sp = data.get('selling_plan');
    /* One-time chosen: drop every selling_plan the hidden widget submitted. */
    var oneTime = ALLOW_ONETIME && (form.getAttribute('data-dn-mode') === 'onetime' || choseOneTime(form));
    if (oneTime) {
      data.delete('selling_plan');
    } else if (!sp || sp === '0' || sp === '') {
      var variantId = String(data.get('id') || '');
      var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      if (variantId) {
        ensurePlan = monthlyPlanFor(m ? m[1] : '', variantId).then(function (best) {
          if (best) data.set('selling_plan', String(best));
        });
      }
    }

    ensurePlan.then(function () {
      return fetch(url('/cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data
      });
    })
      .then(function (res) { return res.json(); })
      .then(function (payload) {
        if (payload.status && payload.status !== 200) throw new Error(payload.description);
        /* Repair quantity BEFORE painting, so no wrong price is ever shown. */
        return reconcile().then(function (changed) {
          if (changed) return refresh();
          if (payload.sections) { paint(payload); return null; }
          return refresh();
        });
      })
      .then(function () { openDrawer(); })
      .catch(function () { form.submit(); })
      .then(function () {
        inFlight = false;
        if (btn) { btn.disabled = false; if (label !== null) btn.innerHTML = label; }
      });
  }

  function isAddForm(form) {
    if (!form || !form.getAttribute) return false;
    return (form.getAttribute('action') || '').indexOf('/cart/add') !== -1;
  }

  /* Capture-phase submit: beats the theme's own submit handler. */
  document.addEventListener('submit', function (e) {
    if (!isAddForm(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    addFromForm(e.target);
  }, true);

  /* Capture-phase click: many themes bind the button directly and never let a
     submit event fire at all, which is why nothing happened before. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[name="add"], .product-add button, button[type="submit"]');
    if (!btn) return;
    var form = btn.form || btn.closest('form');
    if (!isAddForm(form)) return;
    e.preventDefault();
    e.stopPropagation();
    addFromForm(form, btn);
  }, true);

  /* Quiz hand-off: /cart?dn_open=1 (or #dn-cart) opens the sheet on arrival. */
  document.addEventListener('DOMContentLoaded', function () {
    var params = new URLSearchParams(window.location.search);
    if (params.get('dn_open') === '1' || window.location.hash === '#dn-cart') {
      openDrawer();
    }
  });

  window.DNCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refresh
  };
})();
